import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { getFormatedDate } from "../../../../helper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

const GroupedBarLineChart = ({ graphData = [] }) => {
  const [barThickness, setBarThickness] = useState(20);

  // ✅ Responsive bars
  useEffect(() => {
    const resize = () => {
      const w = window.innerWidth;
      if (w < 1000) setBarThickness(8);
      else if (w < 1500) setBarThickness(14);
      else setBarThickness(20);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // ✅ Prepare data
  const labels = graphData.map((item) => getFormatedDate(item?.Date) || "NA");

  const booked = graphData.map((i) => i.BookedAppointments || 0);
  const available = graphData.map((i) => i.AvailableAppointments || 0);
  const utilization = graphData.map((i) =>
    typeof i.Utilization === "string"
      ? parseFloat(i.Utilization.replace("%", ""))
      : i.Utilization || 0,
  );

  const data = {
    labels,
    datasets: [
      {
        label: "Booked Appointments",
        data: booked,
        backgroundColor: "#EC6FBD",
        yAxisID: "appointments", // ✅ SAME AXIS
        type: "bar",
        barThickness,

        borderRadius: {
          topLeft: 10,
          topRight: 10,
        },
        borderSkipped: "bottom",
      },
      {
        label: "Available Appointments",
        data: available,
        backgroundColor: "#A4D65D",
        yAxisID: "appointments", // ✅ SAME AXIS
        type: "bar",
        barThickness,

        borderRadius: {
          topLeft: 10,
          topRight: 10,
        },
        borderSkipped: "bottom",
      },
      {
        label: "Utilization (%)",
        data: utilization,
        borderColor: "#0067A1",
        backgroundColor: "#0067A1",
        yAxisID: "utilization", // ✅ RIGHT AXIS
        type: "line",
        tension: 0,
        pointRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false, // ✅ HEIGHT FIX
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
        },
      },

      tooltip: {
        enabled: true,
        backgroundColor: "#ffffff",
        titleColor: "#111827",
        bodyColor: "#111827",
        borderColor: "#E5E7EB",
        borderWidth: 1,
        cornerRadius: 8,
        padding: {
          top: 10,
          right: 12,
          bottom: 10,
          left: 12,
        },

        displayColors: true,

        boxPadding: 6,

        titleFont: {
          size: 13,
          weight: "500",
        },

        bodyFont: {
          size: 13,
          weight: "400",
        },

        callbacks: {
          title: (items) => {
            // Top line (like "Tempcontrolled")
            return items[0].label;
          },

          label: (ctx) => {
            const value =
              ctx.dataset.label === "Utilization (%)"
                ? `${ctx.parsed.y.toFixed(2)}%`
                : ctx.parsed.y;

            return `${ctx.dataset.label}: ${value}`;
          },
        },
      },
    },

    scales: {
      x: {
        grid: {
          display: false, // ✅ removes vertical boxes
          drawBorder: false,
        },
        ticks: {
          color: "#333",
        },
      },

      appointments: {
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: "Appointments",
        },
        ticks: {
          color: "#333",
        },

        grid: {
          display: false, // ✅ removes horizontal boxes
          drawBorder: false,
        },
      },
      utilization: {
        type: "linear",
        position: "right",
        title: {
          display: true,
          text: "Utilization (%)",
        },
        ticks: {
          callback: (val) => `${val}%`,
        },
        grid: {
          drawOnChartArea: false, // ✅ clean look like screenshot
        },
      },
    },
  };

  return (
    <div className="grouped-bar-line-chart" style={{ height: "400px" }}>
      <Chart type="bar" data={data} options={options} />
    </div>
  );
};

export default GroupedBarLineChart;
